import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return null;
  }
  if (isMobile) {
    return <nav className="flex flex-col items-center gap-8 mt-12">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-2xl font-serif text-[#D4AF37] hover:text-white transition-colors duration-300 uppercase tracking-widest" onClick={closeMobileMenu}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative block py-2 text-[13px] font-sans font-medium uppercase tracking-widest text-[#D4AF37] transition-colors duration-300 hover:text-white">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-[#D4AF37] transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("dark fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out", isScrolled ? "bg-black/90 backdrop-blur-xl border-b border-[#D4AF37]/20 py-3" : "bg-transparent py-6")}>
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between">
        
        <Link to="/HomePage#" className="relative z-50 group flex items-center gap-4">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557449/img/image_weii17vj.png" alt="DJ Taurx Logo" className="h-[1in] md:h-[0.75in] w-auto object-contain" />
        </Link>

        <div className="hidden md:block">
          <MenuProvider menu_id="17">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <Button variant="outline" size="icon" className="md:hidden relative z-50 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none transition-all duration-300" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>

        <div className={cn("fixed inset-0 z-40 bg-black/95 backdrop-blur-xl transition-all duration-500 md:hidden flex flex-col items-center justify-center", isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-full")}>
          <MenuProvider menu_id="17">
            <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}